import { ArrowDown, ArrowRight, ChevronDown } from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { trackClick } from "@/utils/posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl, getOrganizationJourneyContext } from "@/utils/organizationJourney";
import "./HealthcareAICaseStudy.css";

const departments = [
  { name: "Sales & Marketing", work: "Lead scoring, content generation, campaign optimization" },
  { name: "Operations", work: "Process automation, documentation, quality assurance" },
  { name: "Customer Success", work: "Response templates, sentiment analysis, churn prediction" },
  { name: "HR & People", work: "Recruiting workflows, onboarding, policy Q&A" },
  { name: "Finance", work: "Report generation, forecasting, expense analysis" },
  { name: "Product & R&D", work: "User research synthesis, feature ideation, competitive analysis" },
];

const playbookContents = [
  "AI usage policies and governance",
  "Department-specific prompt libraries",
  "Tool selection and integration guides",
  "Quality and validation checklists",
  "ROI tracking and success metrics",
  "Security and compliance guidelines",
];

const changes = [
  { before: "Different understandings of AI", after: "A shared language, from executives to analysts" },
  { before: "Unclear connections to daily work", after: "Department workflows tied to practical applications" },
  { before: "No common framework for adoption", after: "A company playbook and ongoing implementation support" },
];

function StrategyCallLink({ location, attribution, children, className = "" }: {
  location: string;
  attribution: string;
  children: React.ReactNode;
  className?: string;
}) {
  const href = buildOrganizationJourneyUrl("/ai-call", attribution, {
    source: "healthcare-ai-case-study",
    location,
    audience: "organization",
  });

  return (
    <a
      className={`healthcare-case__cta ${className}`}
      href={href}
      onClick={() => trackClick("Healthcare Case Study - Book Strategy Call", {
        location,
        source: "healthcare-ai-case-study",
        audience: "organization",
        home_revision: getOrganizationJourneyContext(attribution)?.home_revision,
        destination: "/ai-call",
      })}
    >
      {children}
      <ArrowRight size={18} aria-hidden="true" />
    </a>
  );
}

const HealthcareAICaseStudy = () => {
  const attribution = useHydratedSearch();

  return (
    <div className="healthcare-case">
      <SEO
        title="Healthcare AI Case Study: 30 Leaders Aligned in 3 Weeks"
        description="How a $75M health tech company aligned 30 leaders around AI with a private program, department workflows, a company playbook, and ongoing support."
        keywords={["healthcare AI", "AI case study", "health tech AI", "AI adoption", "enterprise AI training", "AI literacy"]}
        type="article"
      />

      <a className="healthcare-case__skip" href="#case-study">Skip to case study</a>
      <header className="healthcare-case__header">
        <div className="healthcare-case__container healthcare-case__header-inner">
          <a href="/" aria-label="Tech Leaders home">
            <img src="/orange-logo.png" alt="Tech Leaders" className="healthcare-case__logo" width="234" height="40" />
          </a>
          <StrategyCallLink location="header" attribution={attribution} className="healthcare-case__cta--header">
            Book a strategy call
          </StrategyCallLink>
        </div>
      </header>

      <main id="case-study" tabIndex={-1}>
        <section className="healthcare-case__hero healthcare-case__container" aria-labelledby="case-title">
          <div>
            <p className="healthcare-case__eyebrow">Healthcare / AI adoption case study</p>
            <h1 id="case-title">30 leaders.<br />One shared<br /><em>language for AI.</em></h1>
            <p className="healthcare-case__intro">
              How a $75M health tech company brought executives, managers, and analysts together to put AI into practice in three weeks.
            </p>
            <div className="healthcare-case__hero-actions">
              <StrategyCallLink location="hero" attribution={attribution}>Map your team’s next step</StrategyCallLink>
              <a href="#approach" className="healthcare-case__text-link">See how we did it <ArrowDown size={16} aria-hidden="true" /></a>
            </div>
          </div>
          <aside className="healthcare-case__brief" aria-label="Engagement at a glance">
            <p className="healthcare-case__eyebrow">The engagement</p>
            <p className="healthcare-case__brief-title">A shared foundation.<br />Built around real work.</p>
            <dl>
              <div><dt>Organization</dt><dd>Health tech<br /><span>$75M in revenue</span></dd></div>
              <div><dt>Participants</dt><dd>30 leaders<br /><span>Executives to analysts</span></dd></div>
              <div><dt>Timeline</dt><dd>3 weeks</dd></div>
              <div><dt>Format</dt><dd>Private, custom program</dd></div>
            </dl>
            <p className="healthcare-case__brief-note">Delivered by Tech Leaders</p>
          </aside>
        </section>

        <nav className="healthcare-case__contents" aria-label="Case study sections">
          <div className="healthcare-case__container">
            <a href="#challenge"><span>01</span> The challenge</a>
            <a href="#approach"><span>02</span> What we delivered</a>
            <a href="#outcomes"><span>03</span> What changed</a>
          </div>
        </nav>

        <section id="challenge" className="healthcare-case__section healthcare-case__container healthcare-case__split" aria-labelledby="challenge-title">
          <div>
            <p className="healthcare-case__eyebrow">01 / The challenge</p>
            <h2 id="challenge-title">The technology was there.<br />The alignment wasn’t.</h2>
          </div>
          <div className="healthcare-case__prose">
            <p>The company was growing quickly, but its teams had different understandings of what AI could do. Executives needed clarity. Analysts needed direction. Departments needed a common way to move forward.</p>
            <p>We brought 30 key team members into one private program, with a clear goal: build the shared understanding and practical skills to lead adoption together.</p>
            <ul className="healthcare-case__issues">
              <li>Siloed knowledge across departments</li>
              <li>Fear and hype slowing executive buy-in</li>
              <li>No shared framework or KPIs for adoption</li>
            </ul>
          </div>
        </section>

        <section id="approach" className="healthcare-case__approach" aria-labelledby="approach-title">
          <div className="healthcare-case__container">
            <div className="healthcare-case__section-heading">
              <div>
                <p className="healthcare-case__eyebrow">02 / What we delivered</p>
                <h2 id="approach-title">Learn together.<br />Apply it to the work.</h2>
              </div>
              <p>A private program connected AI fundamentals to department workflows, a company playbook, and continued support.</p>
            </div>
            <ol className="healthcare-case__steps">
              <li className="healthcare-case__step">
                <span className="healthcare-case__step-number" aria-hidden="true">01</span>
                <h3>Get leaders on the same page.</h3>
                <div className="healthcare-case__prose">
                  <p>Level-set managers and the executive team on AI fundamentals. Build practical skills in data configuration, prompting, reviewing outputs, and validating the result.</p>
                  <p className="healthcare-case__takeaway">The focus: a shared language for decisions about AI.</p>
                </div>
              </li>
              <li className="healthcare-case__step">
                <span className="healthcare-case__step-number" aria-hidden="true">02</span>
                <h3>Connect AI to everyday work.</h3>
                <div className="healthcare-case__prose">
                  <p>Design department workflows around the tasks teams already do, connecting AI strategy to operational needs and real KPIs.</p>
                  <details className="healthcare-case__details" onToggle={(event) => {
                    if (event.currentTarget.open) trackClick("Healthcare Case Study - Explore Workflows", { location: "approach" });
                  }}>
                    <summary>Explore the department workflows <ChevronDown size={18} aria-hidden="true" /></summary>
                    <dl className="healthcare-case__departments">
                      {departments.map((department) => (
                        <div key={department.name}><dt>{department.name}</dt><dd>{department.work}</dd></div>
                      ))}
                    </dl>
                  </details>
                </div>
              </li>
              <li className="healthcare-case__step">
                <span className="healthcare-case__step-number" aria-hidden="true">03</span>
                <h3>Give the team a way to keep going.</h3>
                <div className="healthcare-case__prose">
                  <p>A company-specific AI-First Playbook captures the policies, prompts, and validation practices. Ongoing AI Mastermind sessions give leaders a place to work through implementation decisions, review tools, and improve workflows.</p>
                  <details className="healthcare-case__details">
                    <summary>Inside the AI-First Playbook <ChevronDown size={18} aria-hidden="true" /></summary>
                    <ul className="healthcare-case__playbook">
                      {playbookContents.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </details>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section id="outcomes" className="healthcare-case__outcomes" aria-labelledby="outcomes-title">
          <div className="healthcare-case__container">
            <div className="healthcare-case__section-heading">
              <div>
                <p className="healthcare-case__eyebrow">03 / What changed</p>
                <h2 id="outcomes-title">An aligned team.<br />A practical path forward.</h2>
              </div>
              <p>Thirty leaders came through the three-week program with a common foundation for AI adoption, from the executive team to the analysts doing the work.</p>
            </div>
            <table className="healthcare-case__comparison">
              <caption className="sr-only">The team’s starting point and the foundation built through the program</caption>
              <thead><tr><th scope="col">Where they started</th><th scope="col">What they built</th></tr></thead>
              <tbody>
                {changes.map((change) => <tr key={change.before}><td>{change.before}</td><td>{change.after}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>

        <section className="healthcare-case__next healthcare-case__container" aria-labelledby="next-title">
          <div>
            <p className="healthcare-case__eyebrow">Your team’s next step</p>
            <h2 id="next-title">Give your team a<br /><em>shared starting point.</em></h2>
            <p>If your teams are experimenting with AI but struggling to move together, bring one recurring workflow to a strategy call. We’ll discuss your priorities, a useful starting point, and whether training or implementation support fits.</p>
            <StrategyCallLink location="cta_section" attribution={attribution}>Book a strategy call</StrategyCallLink>
            <p className="healthcare-case__call-note">Free 15-minute phone conversation. Discuss your priorities and a useful next step.</p>
          </div>
          <aside className="healthcare-case__call-brief" aria-label="What to bring to the call">
            <p className="healthcare-case__eyebrow">Start with what you know</p>
            <h3>Bring the work.<br />We’ll help map the way.</h3>
            <ol>
              <li><span>01</span> A recurring task your team wants to improve</li>
              <li><span>02</span> What’s getting in the way today</li>
              <li><span>03</span> What a useful result would look like</li>
            </ol>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HealthcareAICaseStudy;
